import {
  MutableRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'preact/hooks';

import { isTapEvent } from '../utils/events';
import { canHover } from '../utils/hover';

import { useJWPlayerEvent } from './useJWPlayerEvent';
import { useTimeoutManager } from './useTimeoutManager';
import { useSeek } from './useSeek';

const INTERACTION_TIMEOUT_IN_MILLISECONDS = 2000;
const SEEK_DIFF_IN_SECONDS = 10;

const defaultIsEventSupported = () => {
  return true;
};

const isActionEvent = (
  e: TouchEvent | MouseEvent | FocusEvent | WheelEvent
) => {
  return (
    e.type === 'click' &&
    e
      .composedPath()
      .some(
        (target) =>
          target instanceof HTMLElement && target.dataset.action === 'true'
      )
  );
};

export const usePlayerInteraction = ({
  layoutRef,
  isEventSupported = defaultIsEventSupported,
  interactionTimeout = INTERACTION_TIMEOUT_IN_MILLISECONDS,
  defaultIsActive = false,
}: {
  layoutRef: MutableRef<HTMLElement>;
  isEventSupported?: (
    e: TouchEvent | MouseEvent | FocusEvent | WheelEvent
  ) => boolean;
  interactionTimeout?: number;
  defaultIsActive?: boolean;
}) => {
  const [isActive, setIsActive] = useState(defaultIsActive);
  const isActiveRef = useRef(isActive);

  const timeoutHandler = useCallback(() => {
    setIsActive(false);
  }, []);

  const { refreshTimeout, stopTimeout, enableTimeout, disableTimeout } =
    useTimeoutManager(timeoutHandler, interactionTimeout);

  useJWPlayerEvent('play', refreshTimeout);
  useJWPlayerEvent('pause', stopTimeout);

  useEffect(() => {
    const interactHandler = (
      e: TouchEvent | MouseEvent | FocusEvent | WheelEvent
    ) => {
      if (!isEventSupported(e)) {
        return;
      }

      stopTimeout();

      if (isActiveRef.current && e && isTapEvent(e) && !isActionEvent(e)) {
        setIsActive(false);

        return;
      }

      setIsActive(true);

      refreshTimeout();
    };

    const element = layoutRef.current;
    const hoverEnabled = canHover();

    const leaveHandler = () => {
      setIsActive(false);
    };

    refreshTimeout();

    if (hoverEnabled) {
      element.addEventListener('mousemove', interactHandler, { passive: true });
      element.addEventListener('mouseleave', leaveHandler);
      element.addEventListener('wheel', interactHandler, { passive: true });
    }

    element.addEventListener('click', interactHandler);
    element.addEventListener('blur', leaveHandler);

    return () => {
      if (hoverEnabled) {
        element.removeEventListener('mousemove', interactHandler);
        element.removeEventListener('mouseleave', leaveHandler);
        element.removeEventListener('wheel', interactHandler);
      }

      element.removeEventListener('click', interactHandler);
      element.removeEventListener('blur', leaveHandler);
    };
  }, [isEventSupported, stopTimeout, refreshTimeout]);

  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  const { rewindBy, fastForwardBy } = useSeek();

  const handleLeftDoubleTap = useCallback(() => {
    rewindBy(SEEK_DIFF_IN_SECONDS);

    refreshTimeout();
  }, [rewindBy, refreshTimeout]);

  const handleRightDoubleTap = useCallback(() => {
    fastForwardBy(SEEK_DIFF_IN_SECONDS);

    refreshTimeout();
  }, [fastForwardBy, refreshTimeout]);

  const handleProgressBarInteraction = useCallback(
    (isProgressBarActive: boolean) => {
      if (isProgressBarActive) {
        disableTimeout();
      } else {
        enableTimeout();
      }
    },
    [disableTimeout, enableTimeout]
  );

  const handleTap = useCallback((e: MouseEvent) => {
    // redispatch caught event to trigger default interaction
    // flow in `usePlayerInteraction` hook
    layoutRef.current.dispatchEvent(e);
  }, []);

  return {
    isActive,
    refreshTimeout,
    enableTimeout,
    disableTimeout,
    handleLeftDoubleTap,
    handleRightDoubleTap,
    handleProgressBarInteraction,
    handleTap,
  };
};
